import React from 'react';
import CheckBoxCustom from '../../../../components/checkbox/CheckBoxCustom';
import { Box, styled } from '@mui/material';

type Props = {
    permission_data: any[];
    onCheckAll: (status: boolean) => void;
    checkAll: boolean;

    onCheckMain: (main_index: number, action_index: number, status: boolean) => void;
    onCheckMainPermit: (main_index: number, status: boolean) => void;

    onCheckSub: (main_index: number, sub_index: number, action_index: number, status: boolean) => void;
    onCheckSubPermit: (main_index: number, sub_index: number, status: boolean) => void;

    onCheckThreeSub: (main_index: number, sub_index: number, threeIndex: number, action_index: number, status: boolean) => void;
    onCheckThreeSubPermit: (main_index: number, sub_index: number, threeIndex: number, status: boolean) => void;

    disabled?: boolean;
};

const PermissionBox = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    border: '2px solid rgba(0,0,0,0.12)',
    borderRadius: '8px',
    padding: '10px 4px',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));

const aggregateState = (node: any, childrenKeys: Array<'subMenu' | 'threeSub'> = []) => {
    const actions = Array.isArray(node?.actions) ? node.actions : [];
    const childPermits: boolean[] = [...actions.map((a: any) => !!a?.permit)];

    childrenKeys.forEach(k => {
        const arr = Array.isArray(node?.[k]) ? node[k] : [];
        childPermits.push(...arr.map((c: any) => !!c?.permit));
    });

    if (childPermits.length === 0) {
        const self = !!node?.permit;
        return { checked: self, indeterminate: false };
    }
    const checkedCount = childPermits.filter(Boolean).length;
    const all = checkedCount === childPermits.length;
    const none = checkedCount === 0;
    return { checked: all, indeterminate: !all && !none };
};

const PermissionCustom: React.FC<Props> = (props: Props) => {
    const {
        permission_data,
        onCheckAll,
        onCheckMain,
        onCheckMainPermit,
        onCheckSub,
        onCheckSubPermit,
        onCheckThreeSub,
        onCheckThreeSubPermit,
        disabled,
    } = props;

    const renderThreeSub = (children3: any[], main_index: number, sub_index: number) => {
        if (!Array.isArray(children3) || children3.length === 0) return null;

        return (
            <section className="w-full gap-1 flex flex-col pl-0 md:pl-10">
                {children3.map((list: any, tIndex: number) => {
                    const actions = Array.isArray(list?.actions) ? list.actions : [];
                    const indeterminate =
                        actions.length > 0 &&
                        !actions.every((x: any) => !!x?.permit) &&
                        actions.some((x: any) => !!x?.permit);

                    return (
                        <React.Fragment key={list.code ?? `t-${main_index}-${sub_index}-${tIndex}`}>
                            <PermissionBox sx={{ padding: '10px 8px' }}>
                                <span className="flex-1">
                                    <CheckBoxCustom
                                        disabled={disabled}
                                        checked={!!list?.permit}
                                        label={`${main_index + 1}.${sub_index + 1}.${tIndex + 1} ${list?.name}`}
                                        setChecked={(st) => onCheckThreeSubPermit(main_index, sub_index, tIndex, st)}
                                        indeterminate={indeterminate}
                                    />
                                </span>
                                <span className="w-full gap-2 flex flex-wrap md:flex-nowrap flex-1 justify-end items-center">
                                    {actions.map((x: any, ac_index: number) => (
                                        <CheckBoxCustom
                                            key={x.code ?? `tac-${ac_index}`}
                                            disabled={disabled}
                                            setChecked={(st) => onCheckThreeSub(main_index, sub_index, tIndex, ac_index, st)}
                                            checked={!!x?.permit}
                                            label={`${x?.name}`}
                                        />
                                    ))}
                                </span>
                            </PermissionBox>
                        </React.Fragment>
                    );
                })}
            </section>
        );
    };

    const renderSubMenu = (subs: any[], main_index: number) => {
        if (!Array.isArray(subs) || subs.length === 0) return null;

        return (
            <section className="w-full gap-1 flex flex-col pl-0 md:pl-10">
                {subs.map((list: any, sub_index: number) => {
                    const agg = aggregateState(list, ['threeSub']);
                    return (
                        <React.Fragment key={list.code ?? `s-${main_index}-${sub_index}`}>
                            <PermissionBox sx={{ padding: '10px 8px' }}>
                                <span className="flex-1">
                                    <CheckBoxCustom
                                        disabled={disabled}
                                        checked={list?.permit ?? agg.checked}
                                        label={`${main_index + 1}.${sub_index + 1} ${list?.name}`}
                                        setChecked={(st) => onCheckSubPermit(main_index, sub_index, st)}
                                        indeterminate={agg.indeterminate}
                                    />
                                </span>
                                <span className="w-full gap-2 flex flex-wrap md:flex-nowrap flex-1 justify-end items-center">
                                    {Array.isArray(list?.actions) &&
                                        list.actions.map((x: any, ac_index: number) => (
                                            <CheckBoxCustom
                                                key={x.code ?? `sac-${ac_index}`}
                                                disabled={disabled}
                                                setChecked={(st) => onCheckSub(main_index, sub_index, ac_index, st)}
                                                checked={!!x?.permit}
                                                label={`${x?.name}`}
                                            />
                                        ))}
                                </span>
                            </PermissionBox>

                            {(() => {
                                const children3 = Array.isArray(list?.threeSub) && list.threeSub.length > 0
                                    ? list.threeSub
                                    : Array.isArray(list?.subMenu) && list.subMenu.length > 0
                                        ? list.subMenu
                                        : [];
                                return renderThreeSub(children3, main_index, sub_index);
                            })()}
                        </React.Fragment>
                    );
                })}
            </section>
        );
    };

    return (
        <div className="w-full">
            <Box my={4}>
                <CheckBoxCustom
                    disabled={disabled}
                    setChecked={(status) => onCheckAll(status)}
                    label={'เลือกทั้งหมด'}
                />
            </Box>

            <section className="w-full flex flex-col gap-2">
                {permission_data?.map((list, index: number) => {
                    const agg = aggregateState(list, ['subMenu']); 
                    return (
                        <React.Fragment key={list.code ?? `m-${index}`}>
                            <PermissionBox>
                                <span className="w-fit">
                                    <CheckBoxCustom
                                        disabled={disabled}
                                        setChecked={(st) => onCheckMainPermit(index, st)}
                                        indeterminate={agg.indeterminate}
                                        checked={list?.permit ?? agg.checked}
                                        label={`${index + 1} ${list?.name}`}
                                    />
                                </span>
                                <span className="w-full gap-2 flex flex-1 flex-wrap md:flex-nowrap justify-end items-center">
                                    {Array.isArray(list?.actions) &&
                                        list.actions.map((x: any, i: number) => (
                                            <CheckBoxCustom
                                                key={x.code ?? `mac-${i}`}
                                                disabled={disabled}
                                                setChecked={(st) => onCheckMain(index, i, st)}
                                                checked={!!x?.permit}
                                                label={`${x?.name}`}
                                            />
                                        ))}
                                </span>
                            </PermissionBox>

                            {Array.isArray(list?.subMenu) && list.subMenu.length > 0 && renderSubMenu(list.subMenu, index)}
                        </React.Fragment>
                    );
                })}
            </section>
        </div>
    );
};

export default PermissionCustom;
